import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, styled, Backdrop, CircularProgress } from '@mui/material';
import theme from '../src/theme/theme';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AppMainStyle } from '../src/theme/AppMainStyle';
import Footer from './layout/footer/Footer';
import AppMenu from './components/AppMenu/menu';
import AppBarHeader from './layout/header/header';
import SnackbarComponent from './components/snackbar/SnackbarComponent';
import './App.css';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { openSnackbar } from './redux/actions/snackbarAction';
import { CyberKey } from './config/apiKeys';
import { API_URL, URL_DETAILS } from './config/apiUrl';
import { useSelector, useDispatch } from 'react-redux';
import { setUserData } from './redux/actions/userDataAction';
import {
  setVendxSessionId,
  setTransScriptSession,
} from './redux/actions/sessionAction';
import MessageDisplay from './components/MessageDisplay/MessageDisplay';
import TermsDialog from './components/TermsDialog';
import TokenExpiryDialog from './components/TokenExpiryDialog';
import ReloadConfirmationDialog from './components/ReloadConfirmationDialog';
import { isBrowser, isMobile, isTablet } from 'react-device-detect';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { isTranscriptRoute, ROUTES } from './routes';
import TranscriberDetail from './pages/TranscriberDetail'; 

// End This code for Sidebar Open close
const MainLayoutDiv = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
  //paddingTop: 'var(--headerHeight)', // Use with Header
  paddingTop: 0, // Use without Header
  paddingLeft: 0, // Use without Sidebar
  //paddingLeft: 64, // Use with Sidebar
  flexGrow: 1,
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: 0,
  },
}));

// Inner App component that uses context
function AppInner() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Get app context
  const {
    details,
    setDetails,
    meetingAPIDetails,
    setMeetingAPIDetails,
    loading,
    setLoading,
    termsConditionFlag,
    setTermsConditionFlag,
    termsAccepted,
    setTermsAccepted,
    formatTime,
    formatDuration,
    formatDateTime,
  } = useAppContext();

  const [unAuthorizedDialoagApi, setUnAuthorizedDialoagApi] = useState(false);
  const [blockedMes, setBlockedMes] = useState(false);
  // Use ref to track if session has been initialized to prevent overwriting
  const sessionInitializedRef = useRef(false);
  const [vendxSessionID, setVendxSessionID] = useState(() => {
    // Initialize from localStorage, but never set to null if localStorage has a value
    const stored = localStorage.getItem('vendxSessionID');
    return stored || null;
  });
  const [vendxApiHost, setVendxApiHost] = useState(() => {
    const stored = localStorage.getItem('apiHost');
    return stored || null;
  });
  const [vendxParentHost, setVendxParentHost] = useState(() => {
    const stored = localStorage.getItem('parentHost');
    return stored || null;
  });
  const [apiError, setApiError] = useState(false);
  const [isForbiddenError, setIsForbiddenError] = useState(false);
  const transScriptSession = useSelector(
    state => state.sessionDetail.transScriptSession
  );
  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenExpiryTime, setTokenExpiryTime] = useState(null);
  
  // Get recording state for mobile redirect logic
  const currentMeetingDetails = useSelector(state => state.currentMeetingDetails);
  const isRecording = useSelector(state => state.currentMeetingDetails.isRecording);
  const isStarted = useSelector(state => state.currentMeetingDetails.isStarted);
  const stopped = useSelector(state => state.currentMeetingDetails.stopped);
  
  // Determine if meeting is ongoing
  const isMeetingOngoing = isRecording || (isStarted && !stopped);

  // Determine current view based on route
  const currentPath = location.pathname;
  const isOnTranscriptRoute = isTranscriptRoute(currentPath);

  // CRITICAL: Initialize session ID FIRST before any redirects
  // This ensures localStorage is preserved even if navigation causes remounts
  useEffect(() => {
    // Prevent re-initialization if already done (handles remounts from navigation)
    if (sessionInitializedRef.current) {
      // On remount, just sync state from localStorage if state is null but localStorage has value
      if (!vendxSessionID) {
        const stored = localStorage.getItem('vendxSessionID');
        if (stored) {
          setVendxSessionID(stored);
          dispatch(setVendxSessionId(stored));
        } else {
          // Remount sync - localStorage is also null
        }
      }
      if (!vendxApiHost) {
        const stored = localStorage.getItem('apiHost');
        if (stored) {
          setVendxApiHost(stored);
        }
      }
      if (!vendxParentHost) {
        const stored = localStorage.getItem('parentHost');
        if (stored) {
          setVendxParentHost(stored);
        }
      }
      return;
    }

    const queryParams = Object.fromEntries(searchParams.entries());
    const hasDataParam = searchParams.has('data');
    const hasPlainParams = searchParams.has('sessionId') || searchParams.has('api_host') || searchParams.has('parent_host');
    if (Object.keys(queryParams).length === 0) {
      // No query params - ensure we have session ID from localStorage
      // Read directly from localStorage to ensure we get the latest value
      const storedSessionID = localStorage.getItem('vendxSessionID');
      const storedApiHost = localStorage.getItem('apiHost');
      const storedParentHost = localStorage.getItem('parentHost');
      
      // Only update state if localStorage has values (never overwrite with null)
      if (storedSessionID) {
        setVendxSessionID(storedSessionID);
        dispatch(setVendxSessionId(storedSessionID));
      } else {
        // No session ID in localStorage and no query params
      }
      if (storedApiHost) {
        setVendxApiHost(storedApiHost);
      }
      if (storedParentHost) {
        setVendxParentHost(storedParentHost);
      }
    } else if (hasPlainParams) {
      // Plain query params exist (not encrypted) - use them directly
      
      const sessionId = searchParams.get('sessionId');
      const apiHost = searchParams.get('api_host');
      const parentHost = searchParams.get('parent_host');
      if (sessionId) {
        localStorage.setItem('vendxSessionID', sessionId);
        setVendxSessionID(sessionId);
        dispatch(setVendxSessionId(sessionId));
      }
      if (apiHost) {
        localStorage.setItem('apiHost', apiHost);
        setVendxApiHost(apiHost);
      }
      if (parentHost) {
        localStorage.setItem('parentHost', parentHost);
        setVendxParentHost(parentHost);
      }

      window.history.replaceState(
        {},
        document.title,
        location.pathname
      );
    } else if (hasDataParam) {
      // Encrypted data param exists - decrypt and set session ID
      try {
        const encrypted = decodeURIComponent(searchParams.get('data') || '');
        const bytes = CryptoJS.AES.decrypt(encrypted, CyberKey);
        const decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));


        if (decrypted && decrypted.hasOwnProperty('sessionId')) {
          localStorage.setItem('vendxSessionID', decrypted.sessionId);
          setVendxSessionID(decrypted.sessionId);
          dispatch(setVendxSessionId(decrypted.sessionId));
        }
        if (decrypted && decrypted.hasOwnProperty('api_host')) {
          localStorage.setItem('apiHost', decrypted.api_host);
          setVendxApiHost(decrypted.api_host);
        }
        if (decrypted && decrypted.hasOwnProperty('parent_host')) {
          localStorage.setItem('parentHost', decrypted.parent_host);
          setVendxParentHost(decrypted.parent_host);
        }

        window.history.replaceState(
          {},
          document.title,
          location.pathname
        );
      } catch (e) {
        // Error decrypting query params
      }
    }

    // Mark as initialized
    sessionInitializedRef.current = true;
    }, []); // Run only once on mount - ref persists across remounts to handle navigation

  // Redirect logic: Handle redirects on reload/first render
  // - Mobile users: redirect to history when on index route OR when on home page (reload on home → history)
  //   Skip if viewing meeting or active recording.
  // - Desktop users: redirect to home only when on index route (/transcribe) (skip if active recording)
  // Do NOT redirect when user is already on a valid sub-route (history, bin, meeting) so reload preserves the page.
  useEffect(() => {
    // Use setTimeout to ensure session initialization completes before redirect
    const redirectTimer = setTimeout(() => {
      const pathname = location.pathname;
      const isIndexRoute = pathname === '/transcribe' || pathname === '/transcribe/';
      const isHomeRoute = pathname === ROUTES.HOME;
      const isMobileDevice = isMobile && !isTablet;
      const isDesktop = !isMobileDevice; // Desktop includes tablets
      const isViewingMeeting = pathname.startsWith('/transcribe/meeting/') || 
                              pathname.startsWith('/transcribe/bin/meeting/');
      const hasActiveRecording = isRecording || (isStarted && !currentMeetingDetails?.stopped);
      
      // Mobile redirect: when on index route OR on home page (reload), send to history
      // Skip redirect if viewing a meeting detail or if there's an active recording
      if (isMobileDevice && 
          (isIndexRoute || isHomeRoute) && 
          !isViewingMeeting && 
          !hasActiveRecording) {
        navigate(ROUTES.HISTORY, { replace: true });
      }
      
      // Desktop redirect: only when on index route, send to home
      // Skip redirect if there's an active recording (to preserve recording state)
      // Do not redirect when already on history, bin, home, or meeting routes (preserves page on reload)
      if (isDesktop && 
          isIndexRoute && 
          !hasActiveRecording) {
        navigate(ROUTES.HOME, { replace: true });
      }
      
    }, 0); // Use setTimeout(0) to defer until after current execution completes

    return () => clearTimeout(redirectTimer);
  }, []); // Run only on mount (first render/reload)


  useEffect(() => {
    // Wait for session initialization to complete before validating
    if (!sessionInitializedRef.current) {
      // Session initialization not complete yet - skipping validation
      return;
    }
    
    // CRITICAL: Only read from localStorage for API call decision
    // This ensures we don't call API with empty values on first render
    const sessionIdFromStorage = localStorage.getItem('vendxSessionID');
    const apiHostFromStorage = localStorage.getItem('apiHost');
    const parentHostFromStorage = localStorage.getItem('parentHost');

    // Sync state from localStorage if state is null but localStorage has values
    if (!vendxSessionID && sessionIdFromStorage) {
      setVendxSessionID(sessionIdFromStorage);
      dispatch(setVendxSessionId(sessionIdFromStorage));
    }
    if (!vendxApiHost && apiHostFromStorage) {
      setVendxApiHost(apiHostFromStorage);
    }
    if (!vendxParentHost && parentHostFromStorage) {
      setVendxParentHost(parentHostFromStorage);
    }

    // Only call API if both sessionId and apiHost are available in localStorage
    // This prevents calling API with empty values on first render
    if (sessionIdFromStorage && apiHostFromStorage && parentHostFromStorage) {
      fetchValidateAndLogin();
    } else {
      setUnAuthorizedDialoagApi(true);
    }
    
  }, [vendxSessionID, vendxApiHost, vendxParentHost, dispatch]);

  useEffect(() => {
    if (transScriptSession) {
      fetchData();
    }
  }, [transScriptSession]);

  // Check token expiry on mount and restore expiry time from localStorage
  useEffect(() => {
    const storedExpiryTime = localStorage.getItem('tokenExpiryTime');
    if (storedExpiryTime) {
      const expiryTime = parseInt(storedExpiryTime, 10);
      setTokenExpiryTime(expiryTime);
      // Check if token is already expired on mount
      if (Date.now() >= expiryTime) {
        setTokenExpired(true);
      }
    }
  }, []);

  // Monitor token expiry
  useEffect(() => {
    if (!tokenExpiryTime) return;

    const checkTokenExpiry = () => {
      const currentTime = Date.now();
      if (currentTime >= tokenExpiryTime) {
        setTokenExpired(true);
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiry, 30000);

    return () => clearInterval(interval);
  }, [tokenExpiryTime]);

  useEffect(() => {
    const TAB_ID = `${Date.now()}-${Math.random()}`;
    const TABS_KEY = 'openTabs';
    const SHARED_KEY = 'transcriptSession';
    // const SHARED_KEY2 = "vendxSessionID";

    // ✅ SAFETY CHECK: clean stale data if no tabs are registered
    const existingTabs = JSON.parse(localStorage.getItem(TABS_KEY) || '[]');
    if (existingTabs.length === 0) {
      localStorage.removeItem(SHARED_KEY);
      // localStorage.removeItem(SHARED_KEY2);
    }

    // Register this tab
    const registerTab = () => {
      const tabs = JSON.parse(localStorage.getItem(TABS_KEY) || '[]');
      tabs.push(TAB_ID);
      localStorage.setItem(TABS_KEY, JSON.stringify(tabs));
    };

    // Unregister on unload/unmount
    const unregisterTab = () => {
      const tabs = JSON.parse(localStorage.getItem(TABS_KEY) || '[]');
      const remainingTabs = tabs.filter(id => id !== TAB_ID);
      localStorage.setItem(TABS_KEY, JSON.stringify(remainingTabs));

      if (remainingTabs.length === 0) {
        localStorage.removeItem(SHARED_KEY);
        // localStorage.removeItem(SHARED_KEY2);
      }
    };

    // Initialize
    registerTab();
    window.addEventListener('beforeunload', unregisterTab);

    return () => {
      window.removeEventListener('beforeunload', unregisterTab);
      unregisterTab(); // handle React navigation
    };
  }, []);

  // Handle page reload/close warning when meeting is ongoing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isMeetingOngoing) {
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers require a return value
      }
    };

    if (isMeetingOngoing) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isMeetingOngoing]);

  const fetchValidateAndLogin = async () => {    
    // CRITICAL: Read directly from localStorage - API should only be called if values exist in localStorage
    const sessionIdFromStorage = localStorage.getItem('vendxSessionID');
    const apiHostFromStorage = localStorage.getItem('apiHost');
    const parentHostFromStorage = localStorage.getItem('parentHost');  
    // Guard: Don't proceed if required values are missing from localStorage
    if (!sessionIdFromStorage || !apiHostFromStorage) {
     setUnAuthorizedDialoagApi(true);
      return;
    }
    
    const url = `${API_URL}/api/auth/validate-and-login`;
  
    // Sanitize host - use localStorage value
    const sanitizedHost = (apiHostFromStorage || '').trim().replace(/\s+/g, '');
  
    // Sanitize session token - use localStorage value
    const sanitizedSessionID = (sessionIdFromStorage || '').trim().replace(/\s+/g, '');
  
    // Sanitize validation URL
    const validation_url = `${sanitizedHost}/v1/users/me`.replace(/\s+/g, '');
  
    if (!sanitizedSessionID || !sanitizedHost) {
      setUnAuthorizedDialoagApi(true);
      return;
    }
  
    setLoading(true);
    try {
      const value = await axios.post(url, {
        session_token: sanitizedSessionID,
        validation_url: validation_url,
      });
      
  
      dispatch(setTransScriptSession(value.data.access_token));
      localStorage.setItem('transcriptSession', value.data.access_token);
  
      if (value.data.expires_in) {
        const expiryTimestamp = Date.now() + value.data.expires_in * 1000;
        setTokenExpiryTime(expiryTimestamp);
        localStorage.setItem('tokenExpiryTime', expiryTimestamp.toString());
      }
  
      setUnAuthorizedDialoagApi(false);
      setTokenExpired(false);
      setIsForbiddenError(false);
    } catch (error) {
      
      if (error.status === 403) {
        setIsForbiddenError(true);
        setUnAuthorizedDialoagApi(true);
      } else if (error.status === 401) {
        setIsForbiddenError(false);
        setUnAuthorizedDialoagApi(true);
      } else if ([503, 404].includes(error.status)) {
        let message = error.status === 503 ? 'Service Unavailable.' : 'API not found.';
        dispatch(openSnackbar('error', message));
        setApiError(true);
      }
  
      if (error.message === 'Network Error' && !blockedMes) {
        setUnAuthorizedDialoagApi(true);
        setBlockedMes(true);
      }
  
      // Error fetching validate and login details
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const url = `${API_URL}/api/me`;
    try {
      const value = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${transScriptSession}`,
        },
      });

      var values = value.data;

      dispatch(setUserData(value.data));
      if (!value.data.has_accepted_terms) {
        setTermsAccepted(false);
        setTermsConditionFlag(true);
      } else {
        setTermsAccepted(true);
      }
      setIsForbiddenError(false);
    } catch (error) {
      if (error.status === 403) {
        setIsForbiddenError(true);
        setUnAuthorizedDialoagApi(true);
      } else if (error.status === 401) {
        setIsForbiddenError(false);
        setUnAuthorizedDialoagApi(true);
      } else if ([503, 404].includes(error.status)) {
        var message = 'API not found.';
        if (error.status === 503) {
          message = 'Service Unavailable.';
        }
        dispatch(openSnackbar('error', message));
        setApiError(true);
      }
      if (error.message === 'Network Error' && !blockedMes) {
        setUnAuthorizedDialoagApi(true);
        setBlockedMes(true);
      }
      // Error fetching user details
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSession = async () => {
    setTokenExpired(false);
    await fetchValidateAndLogin();
  };

  const handleAcceptTermsClick = async () => {
    const url = `${API_URL}/api/me/accept-terms`;
    setLoading(true);
    try {
      const value = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${transScriptSession}`,
          },
        }
      );

      dispatch(setUserData(value.data));
      if (!value.data.has_accepted_terms) {
        setTermsAccepted(false);
        setTermsConditionFlag(true);
      } else {
        setTermsAccepted(true);
      }
      setIsForbiddenError(false);
    } catch (error) {
      if (error.status === 403) {
        setIsForbiddenError(true);
        setUnAuthorizedDialoagApi(true);
      } else if (error.status === 401) {
        setIsForbiddenError(false);
        setUnAuthorizedDialoagApi(true);
      } else if ([503, 404].includes(error.status)) {
        var message = 'API not found.';
        if (error.status === 503) {
          message = 'Service Unavailable.';
        }
        dispatch(openSnackbar('error', message));
        setApiError(true);
      }
      if (error.message === 'Network Error' && !blockedMes) {
        setUnAuthorizedDialoagApi(true);
        setBlockedMes(true);
      }
      // Error updating user terms
    } finally {
      setLoading(false);
      setTermsConditionFlag(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline>
        {AppMainStyle}
        <Box
          sx={{
            bgcolor: 'var(--grey-50)',
            height: '100vh',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AppBarHeader setTermsConditionFlag={setTermsConditionFlag} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'row' },
              flexGrow: 1,
              pb: isBrowser ? '33px' : 0,
              px: '0 !important',
              flex: '1 1 auto',
              height: 'calc(100% - 58px)',
            }}
          >
            <AppMenu
              setTermsConditionFlag={setTermsConditionFlag}
            />
            {unAuthorizedDialoagApi || !termsAccepted || apiError ? (
              <Box sx={{ width: '100%' }}>
                <MessageDisplay
                  unAuthorizedDialoagApi={unAuthorizedDialoagApi}
                  urlData={[
                    URL_DETAILS['api_host'],
                    URL_DETAILS['current_host'],
                  ]}
                  blockedMes={blockedMes}
                  apiError={apiError}
                  termsAccepted={termsAccepted}
                  setTermsConditionFlag={setTermsConditionFlag}
                />
                <SnackbarComponent />
              </Box>
            ) : (
              <MainLayoutContent
                isOnTranscriptRoute={isOnTranscriptRoute}
                loading={loading}
              />
            )}
            <TermsDialog
              open={termsConditionFlag}
              termsAccepted={termsAccepted}
              onClose={() => setTermsConditionFlag(false)}
              handleAcceptTermsClick={handleAcceptTermsClick}
            />
            <TokenExpiryDialog
              open={tokenExpired && !isForbiddenError}
              onRefresh={handleRefreshSession}
            />
          </Box>
        </Box>
        <Footer />
      </CssBaseline>
    </ThemeProvider>
  );
}

// Main layout content - TranscriberDetail is ALWAYS mounted (just hidden/shown via CSS)
// This preserves WebSocket connections and recording state during navigation
function MainLayoutContent({ isOnTranscriptRoute, loading }) {
  return (
    <MainLayoutDiv className="main-layout">
      <Box
        component="main"
        sx={{
          background: 'var(--grey-0)',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', flex: 1, height: '100%' }}>
          {/* TranscriberDetail is ALWAYS mounted - only hidden/shown via CSS
              This preserves WebSocket connections and all state during navigation */}
          <Box sx={{ display: isOnTranscriptRoute ? 'flex' : 'none', flex: 1 }}>
            <TranscriberDetail />
          </Box>

          {/* Outlet renders history/bin pages - only shown when NOT on transcript route */}
          <Box sx={{ display: isOnTranscriptRoute ? 'none' : 'flex', flex: 1 }}>
            <Outlet />
          </Box>
        </Box>

        <SnackbarComponent />
        <Backdrop
          style={{ color: '#fff', zIndex: '10000000000' }}
          open={loading}
        >
          <CircularProgress style={{ color: '#fff' }} />
        </Backdrop>
      </Box>
    </MainLayoutDiv>
  );
}

// Wrapper component that provides context
export default function App() {
  return (
    <AppProvider>
      <AppInner />
    </AppProvider>
  );
}
